import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ChartsAxisContentProps, ChartsItemContentProps } from '@mui/x-charts';
import { getLabel } from '@mui/x-charts/internals';
import { ChartGroupMeta, formatWidgetValue, GroupPart } from 'utils/fleet-filters';

export interface FleetTooltipSeries {
  label: string;
  value: string;
  color: string;
}

export const FleetChartTooltipBody: React.FC<{
  parts: GroupPart[];
  rowCount: number;
  series: FleetTooltipSeries[];
}> = ({ parts, rowCount, series }) => {
  const { t } = useTranslation();

  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1, maxWidth: 320 }}>
      <Stack spacing={0.25}>
        {parts.map((part) => (
          <Stack key={part.field} direction="row" spacing={1} justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t('fleet-management-field-' + part.field)}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {part.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ my: 0.75 }} />

      <Stack spacing={0.25}>
        {series.map((entry) => (
          <Stack key={entry.label} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: entry.color }}
            />
            <Typography variant="caption" sx={{ flexGrow: 1 }}>
              {entry.label}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {entry.value}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {t('label-chart-tooltip-rows', { count: rowCount })}
      </Typography>
    </Paper>
  );
};

/**
 * Axis trigger: one hover reports which group the mark belongs to and what
 * every series says about it.
 */
export const createFleetAxisTooltipContent =
  (groupMeta: ChartGroupMeta[]): React.FC<ChartsAxisContentProps> =>
  ({ dataIndex, series }) => {
    if (dataIndex == null) return null;

    const meta = groupMeta[dataIndex];
    if (meta == null) return null;

    return (
      <FleetChartTooltipBody
        parts={meta.parts}
        rowCount={meta.rowCount}
        series={series.map((entry) => {
          // Pie series carry no series-level label (only per-slice); the axis trigger never
          // actually reports a pie series, but the type is still part of the union.
          const label = entry.type === 'pie' ? '' : (getLabel(entry.label, 'tooltip') ?? '');
          return {
            label,
            value: formatWidgetValue((entry.data as any[])?.[dataIndex]),
            color: entry.getColor(dataIndex),
          };
        })}
      />
    );
  };

/** Item trigger, used by the pie chart where one slice is one group. */
export const createFleetItemTooltipContent =
  (groupMeta: ChartGroupMeta[]): React.FC<ChartsItemContentProps<'pie'>> =>
  ({ itemData, series, getColor }) => {
    const dataIndex = itemData.dataIndex;
    if (dataIndex == null) return null;

    const meta = groupMeta[dataIndex];
    if (meta == null) return null;

    const slice = series.data[dataIndex];

    return (
      <FleetChartTooltipBody
        parts={meta.parts}
        rowCount={meta.rowCount}
        series={[
          {
            // Pie series have no series-level label — only each slice carries one — so there
            // is no series.label fallback to read here (MUI's own DefaultChartsItemTooltipContent
            // resolves the pie case the same way).
            label: getLabel(slice?.label, 'tooltip') ?? '',
            value: formatWidgetValue(slice?.value),
            color: getColor(dataIndex),
          },
        ]}
      />
    );
  };
